import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { Dialog } from '@/shared/ui/dialogs/Dialog';
import { Input } from '@/shared/ui/inputs/Input';
import { Button } from '@/shared/ui/buttons/Button';
import { useMutate } from '@/shared/hooks/useMutate';
import { useToast } from '@/shared/hooks/useToast';
import { queryKeys } from '@/shared/api/queryKeys';
import {
  apiCategoriesPost,
  apiCategoriesPut,
} from '@/entities/categories';
import {
  categorySchema,
  type CategoryFormValues,
} from '@/features/categories/modules/types';
import type { ApiCategoriesGetItem } from '@/shared/types/api/categories';

type Props = {
  open: boolean;
  editing: ApiCategoriesGetItem | null;
  onClose: () => void;
};

export const FeaturesCategoriesCategoryFormDialog = ({ open, editing, onClose }: Props) => {
  const { t } = useTranslation();
  const toast = useToast();
  const form = useForm<CategoryFormValues>({
    resolver: zodResolver(categorySchema),
    defaultValues: { name_uz: '', name_ru: '', name_en: '', name_kk: '' },
  });

  useEffect(() => {
    if (open) {
      form.reset({
        name_uz: editing?.name_uz ?? '',
        name_ru: editing?.name_ru ?? '',
        name_en: editing?.name_en ?? '',
        name_kk: editing?.name_kk ?? '',
      });
    }
  }, [open, editing, form]);

  const create = useMutate({
    api: (v: CategoryFormValues) => apiCategoriesPost(v),
    invalidateKeys: [queryKeys.categories.all],
    onSuccess: () => {
      toast.success(t('categories.toast.created'));
      onClose();
    },
    onError: (_e, m) => toast.error(m),
  });

  const update = useMutate({
    api: (v: CategoryFormValues) => apiCategoriesPut(editing?.id ?? '', v),
    invalidateKeys: [queryKeys.categories.all],
    onSuccess: () => {
      toast.success(t('categories.toast.updated'));
      onClose();
    },
    onError: (_e, m) => toast.error(m),
  });

  const isPending = create.isPending || update.isPending;
  const nameUzError = form.formState.errors.name_uz?.message;
  const nameRuError = form.formState.errors.name_ru?.message;
  const nameEnError = form.formState.errors.name_en?.message;
  const nameKkError = form.formState.errors.name_kk?.message;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={editing ? t('categories.form.editTitle') : t('categories.form.createTitle')}
      description={t('categories.form.description')}
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={isPending}>
            {t('common.cancel')}
          </Button>
          <Button
            loading={isPending}
            onClick={form.handleSubmit((v) => (editing ? update.mutate(v) : create.mutate(v)))}
          >
            {t('common.save')}
          </Button>
        </>
      }
    >
      <div className="flex flex-col gap-3">
        <Input
          label={t('categories.form.nameUz')}
          required
          autoFocus
          error={nameUzError ? t(nameUzError) : undefined}
          {...form.register('name_uz')}
        />
        <Input
          label={t('categories.form.nameRu')}
          error={nameRuError ? t(nameRuError) : undefined}
          {...form.register('name_ru')}
        />
        <Input
          label={t('categories.form.nameEn')}
          error={nameEnError ? t(nameEnError) : undefined}
          {...form.register('name_en')}
        />
        <Input
          label={t('categories.form.nameKk')}
          error={nameKkError ? t(nameKkError) : undefined}
          {...form.register('name_kk')}
        />
      </div>
    </Dialog>
  );
};
